import store from '@/store';
import { api } from '@/utils/api';

const state = {
  items: [],
  selectedItem: null,
  totalItems: 0,
  loading: false,
  error: null,
};

const getters = {
  getItems: (state) => state.items,
  getSelectedItem: (state) => state.selectedItem,
  getTotalItems: (state) => state.totalItems,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
};

const actions = {
  // Fetch items with pagination
  async fetchItems({ commit }, { businessID, page = 1, perPage = 10, search = '' }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      console.log('fetchItems API call with params:', { businessID, page, perPage, search });

      // Try different parameter structures based on API expectations
      const params = {
        pageNumber: page,
        pageSize: perPage,
        searchTerm: search,
        // Also try query parameters as fallback
        page,
        perPage,
        search,
        q: search,
      };

      const response = await api.get(`/ItemsFBR/GetByPagination/${businessID}`, { params });

      console.log('fetchItems API response:', response.data);

      if (response.data.status === 'success') {
        commit('SET_ITEMS', response.data.data.items);
        commit('SET_TOTAL_ITEMS', response.data.data.pagination.totalCount);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch items');
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch items');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  fetchItemsByIds(ctx, ids) {
    return new Promise((resolve, reject) => {
      const businessID = store.state.user.businessID;
      const userID = store.state.user.userID;

      api
        .post(`/ItemsFBR/LoadMultipleItems/${businessID}?userID=${userID}`, ids)
        .then((response) => {
          console.log('=== SalesInvoiceStoreModule response', response.data);
          if ('status' in response.data && response.data.status === 'success') {
            resolve(response.data.data);
          } else {
            reject('Error fetching Items');
          }
        })
        .catch((error) => reject(error));
    });
  },

  // Fetch single item for edit
  async fetchItem({ commit }, { businessID, id, userID }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const response = await api.get(`/ItemsFBR/LoadSelectedData/${businessID}`, {
        params: { id, userID },
      });

      console.log('fetchItem API response:', response.data);

      if (response.data.status === 'success') {
        commit('SET_SELECTED_ITEM', response.data.data);
        return response.data.data; // Return the data
      } else {
        throw new Error(response.data.message || 'Failed to fetch item');
      }
    } catch (error) {
      console.error('Error fetching item:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch item');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Add item
  saveItem({ commit }, payload) {
    const businessID = store.state.user.businessID;
    const userID = store.state.user.userID;

    if (!userID) {
      throw new Error('User ID is required');
    }

    if (!businessID) {
      throw new Error('Business ID is required');
    }

    return new Promise((resolve, reject) => {
      api
        .post(`/ItemsFBR/Save`, {
          ...payload,
          itemsFBR: {
            ...payload.itemsFBR,
            businessID,
          },
          userID,
        })
        .then((response) => {
          console.log('=== ItemsStoreModule saveItem response', response);
          if ('status' in response.data) {
            console.log('=== status exists');

            if (response.data.status === 'success') {
              console.log('=== status success');
              resolve(response.data.data.id);
            } else {
              console.log('=== status not success');
              reject(new Error(response.data.message || 'Failed to create item'));
            }
          } else {
            console.log('=== status does not exist');
            reject(new Error(response.data.message || 'Failed to create item'));
          }
        })
        .catch((error) => reject(error));
    });
  },

  // Edit item
  async editItem({ commit }, payload) {
    const userID = store.state.user.userID;
    if (!userID) {
      throw new Error('User ID is required');
    }

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      // Extract the itemsFBR data from the payload
      const itemData = payload.itemsFBR || payload;

      const apiPayload = {
        itemsFBR: {
          id: itemData.id,
          itemCode: itemData.itemCode,
          itemName: itemData.itemName,
          unitID: itemData.unitID,
          itemGroupID: itemData.itemGroupID,
          itemPurchaseRate: Number(itemData.itemPurchaseRate) || 0,
          tradePrice: Number(itemData.tradePrice) || 0,
          retailPrice: Number(itemData.retailPrice) || 0,
          salesTaxPercent: Number(itemData.salesTaxPercent) || 0,
          addSalesTaxPercent: Number(itemData.addSalesTaxPercent) || 0,
          itemHSCodeID: itemData.itemHSCodeID,
          isActive: itemData.isActive ?? true,
          businessID: itemData.businessID,
        },
        userID: payload.userID || userID,
      };

      const response = await api.post('/ItemsFBR/Save', apiPayload);

      if (response.data.status === 'success') {
        commit('SET_ERROR', null);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      commit('SET_ERROR', error.message || 'Failed to update item');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Delete item
  async deleteItem({ commit }, { businessID, id }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const userID = store.state.user.userID || store.state.user.id;

      if (!userID) {
        throw new Error('User ID is required for deletion');
      }

      console.log('Deleting item with params:', { businessID, id, userID });

      const response = await api.delete(`/ItemsFBR/Delete/${businessID}?id=${id}&userID=${userID}`);

      if (response.data.status === 'success') {
        commit('SET_ERROR', null);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      commit('SET_ERROR', error.message || 'Failed to delete item');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Export items
  async exportItems({ commit }, { businessID }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      // First request to get the file path
      const response = await api.get(`/ItemsFBR/Export/${businessID}`);

      console.log('Export API response:', response.data);

      if (response.data.status === 'success' && response.data.data.filePath) {
        // Second request to download the actual file
        const filePath = response.data.data.filePath;

        // If the filePath is relative, we need to construct the full URL
        // The API base URL is typically something like https://api-ibico.cloudious.net
        const baseURL = process.env.VUE_APP_API_URL || 'https://api-ibico.cloudious.net';
        const fullFilePath = filePath.startsWith('http') ? filePath : `${baseURL}${filePath}`;

        console.log('Downloading file from:', fullFilePath);

        const downloadResponse = await api.get(fullFilePath, {
          responseType: 'blob',
        });

        commit('SET_ERROR', null);
        return downloadResponse;
      } else {
        throw new Error(response.data.message || 'Failed to export items');
      }
    } catch (error) {
      console.error('Error exporting items:', error);
      commit('SET_ERROR', error.message || 'Failed to export items');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Bulk Upload Items
  bulkUploadItems(ctx, itemsData) {
    const businessID = store.state.user.businessID;
    const userID = store.state.user.userID;

    return new Promise((resolve, reject) => {
      api
        .post(`/ItemsFBR/BulkUpload/${businessID}?userID=${userID}`, itemsData)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    });
  },

  // Clear selected item
  clearSelectedItem({ commit }) {
    commit('SET_SELECTED_ITEM', null);
  },

  // Clear error
  clearError({ commit }) {
    commit('SET_ERROR', null);
  },
};

const mutations = {
  SET_ITEMS(state, items) {
    state.items = items;
  },
  SET_SELECTED_ITEM(state, item) {
    state.selectedItem = item;
  },
  SET_TOTAL_ITEMS(state, total) {
    state.totalItems = total;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
